import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { MaskedLines, Reveal, ParallaxImage } from "@/components/motion";
import { useSite, useUrl } from "@/context/SiteContext";
import { SmartLink } from "@/components/SmartLink";

/**
 * Shared editorial page used by the four "Digital" sub-services
 * (Fezabilitate digitală, Digitalizare, Marketing, Dezvoltare website).
 *
 * Design language matches the Department detail page:
 *   · Chivo display titles
 *   · IBM Plex Sans body
 *   · Mono labels (IBM Plex Mono)
 *   · Full-width alternating black/white sections
 *   · Scroll progress bar + masked line hero reveal
 */
export default function ServicePage({
  number,       // "01" · "02" · "03" · "04"
  eyebrow,      // small mono label above title
  title,        // hero title lines (array of strings)
  tagline,      // 1-line paragraph directly under title
  intro,        // optional array of longer intro paragraphs
  bullets,      // optional array of short bullets (e.g. arhivare / comunicare)
  programs,     // optional array of EU-programs strings
  steps,        // array of {title, desc} — usually 3 items (how-it-works)
  stepsEyebrow, // small label above the steps block
  stepsTitle,   // display title above the steps block
  services,     // array of {title, tags, desc, list?}
  servicesEyebrow,
  servicesTitle,
  criteria,        // optional array of short strings (e.g. DESI criteria)
  criteriaEyebrow,
  criteriaTitle,
  linkGrid,        // optional array of {n, title, desc, to} — renders a linked grid (hub pages)
  linkGridEyebrow,
  linkGridTitle,
  backLabel,       // breadcrumb label + link override (defaults to Digital)
  backTo,
  heroImage,       // optional parallax hero image URL — renders below hero title
}) {
  useEffect(() => {
    document.title = `${(Array.isArray(title) ? title.join(" ") : title) || "Serviciu"} — SUNTEC Group`;
    return () => { document.title = "SUNTEC Group"; };
  }, [title]);

  const { scrollYProgress } = useScroll();
  const progressWidth = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });
  const url = useUrl();
  const { t } = useSite();

  const heroLines = Array.isArray(title) ? title : [title];

  return (
    <div data-testid="service-page">
      {/* Scroll progress bar */}
      <motion.div
        style={{ scaleX: progressWidth }}
        className="fixed top-0 left-0 right-0 h-[2px] bg-black origin-left z-50"
        aria-hidden
      />

      {/* Breadcrumb — back to parent dept */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 pt-10">
        <Link
          to={backTo || url.dept("digital")}
          data-testid="back-to-parent"
          className="font-mono-label inline-flex items-center gap-2 hover:underline"
        >
          <ArrowLeft size={14} /> {backLabel || "Digital"}
        </Link>
      </div>

      {/* HERO */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-12 border-b border-black">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-mono-label text-black/60"
            >
              {number} / {eyebrow}
            </motion.div>
          </div>
          <div className="lg:col-span-10">
            <h1 className="font-display font-medium uppercase text-5xl md:text-8xl tracking-tighter leading-[0.9]">
              <MaskedLines lines={heroLines} delay={0.15} />
            </h1>
            {tagline && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.5 + heroLines.length * 0.11 }}
                className="mt-10 text-base md:text-lg max-w-3xl text-black/80 leading-snug"
              >
                {tagline}
              </motion.p>
            )}
          </div>
        </div>
      </section>

      {/* Parallax hero image */}
      {heroImage && (
        <section className="relative border-b border-black">
          <div className="relative h-[55vh] md:h-[75vh] overflow-hidden">
            <ParallaxImage src={heroImage} alt={heroLines.join(" ")} />
            <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 font-mono-label text-white mix-blend-difference">
              [ {number} / SUNTEC GROUP ]
            </div>
          </div>
        </section>
      )}

      {/* INTRO + BULLETS + PROGRAMS */}
      {(intro?.length > 0 || bullets?.length > 0 || programs?.length > 0) && (
        <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 border-b border-black">
          {intro?.length > 0 && (
            <Reveal className="md:col-span-7">
              <div className="space-y-6 text-base md:text-[17px] text-black/80 leading-relaxed max-w-3xl">
                {intro.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </Reveal>
          )}
          <div className="md:col-span-5 space-y-10">
            {bullets?.length > 0 && (
              <Reveal delay={0.1}>
                <div className="font-mono-label text-black/60 mb-4">Ce înseamnă</div>
                <ul className="space-y-4">
                  {bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-base text-black/85 leading-relaxed">
                      <Check size={16} className="mt-1 shrink-0 opacity-60" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
            {programs?.length > 0 && (
              <Reveal delay={0.15}>
                <div className="font-mono-label text-black/60 mb-4">Programe UE compatibile</div>
                <div className="flex flex-wrap gap-2">
                  {programs.map((p, i) => (
                    <span key={i} className="inline-block border border-black px-4 py-2 text-sm">
                      {p}
                    </span>
                  ))}
                </div>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* HOW IT WORKS — inverted black block */}
      {steps?.length > 0 && (
        <section className="border-b border-black bg-black text-white">
          <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 pt-16 pb-6">
            {stepsEyebrow && (
              <div className="font-mono-label text-white/50">{stepsEyebrow}</div>
            )}
            {stepsTitle && (
              <h2 className="mt-4 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9] max-w-3xl">
                {stepsTitle}
              </h2>
            )}
          </div>
          <div className="mx-auto max-w-[1440px] grid grid-cols-1 md:grid-cols-3 gap-px bg-white/20 border-t border-white/20">
            {steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="bg-black p-8 md:p-12"
                data-testid={`service-step-${i}`}
              >
                <div className="font-mono-label text-white/40">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-4 font-display font-medium uppercase text-3xl md:text-4xl tracking-tight">
                  {s.title}
                </div>
                <p className="mt-4 text-base text-white/70 leading-relaxed">
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* LINK GRID — for hub pages with sub-services */}
      {linkGrid?.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-b border-black">
          <Reveal className="mb-12">
            {linkGridEyebrow && (
              <div className="font-mono-label text-black/60">{linkGridEyebrow}</div>
            )}
            {linkGridTitle && (
              <h2 className="mt-3 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9] max-w-3xl">
                {linkGridTitle}
              </h2>
            )}
          </Reveal>
          <div
            className={
              "grid grid-cols-1 gap-px bg-black border border-black " +
              (linkGrid.length === 2
                ? "md:grid-cols-2"
                : linkGrid.length === 4
                ? "md:grid-cols-2 lg:grid-cols-4"
                : "md:grid-cols-3")
            }
          >
            {linkGrid.map((c, i) => (
              <motion.div
                key={c.to}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
              >
                <Link
                  to={c.to}
                  data-testid={`linkgrid-${i}`}
                  className="group bg-white hover:bg-black hover:text-white transition-all p-8 md:p-10 min-h-[260px] flex flex-col justify-between h-full"
                >
                  <div className="flex items-start justify-between">
                    <div className="font-mono-label opacity-60">{c.n}</div>
                    <ArrowUpRight
                      size={22}
                      className="opacity-40 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-500"
                    />
                  </div>
                  <div>
                    <div className="font-display font-medium uppercase text-3xl md:text-4xl tracking-tighter leading-[0.95]">
                      {c.title}
                    </div>
                    {c.desc && (
                      <div className="mt-3 text-base opacity-70 leading-relaxed max-w-md">
                        {c.desc}
                      </div>
                    )}
                    {c.tags && (
                      <div className="mt-4 font-mono-label opacity-50 text-[10px]">
                        {c.tags}
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* CRITERIA — dense numbered list (used for DESI-style checklists) */}
      {criteria?.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-b border-black">
          <Reveal className="mb-14">
            {criteriaEyebrow && (
              <div className="font-mono-label text-black/60">{criteriaEyebrow}</div>
            )}
            {criteriaTitle && (
              <h2 className="mt-3 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9] max-w-3xl">
                {criteriaTitle}
              </h2>
            )}
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black border border-black">
            {criteria.map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.04 }}
                className="bg-white p-6 md:p-8 flex items-start gap-5"
                data-testid={`criteria-item-${i}`}
              >
                <div className="font-mono-label text-black/40 shrink-0 pt-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="text-base text-black/85 leading-relaxed">{c}</div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* SERVICES */}
      {services?.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-b border-black">
          <Reveal className="mb-14">
            {servicesEyebrow && (
              <div className="font-mono-label text-black/60">{servicesEyebrow}</div>
            )}
            {servicesTitle && (
              <h2 className="mt-3 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9] max-w-3xl">
                {servicesTitle}
              </h2>
            )}
          </Reveal>
          <div className="border-t border-black">
            {services.map((s, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.05 }}
                className="border-b border-black py-10 md:py-14 grid grid-cols-1 md:grid-cols-12 gap-8"
                data-testid={`service-item-${i}`}
              >
                <div className="md:col-span-2">
                  <div className="font-mono-label text-black/50">
                    {String(i + 1).padStart(2, "0")} / Serviciu
                  </div>
                </div>
                <div className="md:col-span-5">
                  <h3 className="font-display font-medium uppercase text-2xl md:text-4xl tracking-tighter leading-[0.95]">
                    {s.title}
                  </h3>
                  {s.tags && (
                    <div className="mt-4 font-mono-label text-black/60 text-[11px] leading-relaxed">
                      {s.tags}
                    </div>
                  )}
                </div>
                <div className="md:col-span-5 space-y-4 text-base text-black/80 leading-relaxed max-w-2xl">
                  {(Array.isArray(s.desc) ? s.desc : [s.desc]).map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                  {s.list && (
                    <ol className="mt-2 space-y-2 list-decimal list-inside marker:font-mono marker:text-black/50">
                      {s.list.map((l, j) => (
                        <li key={j} className="text-base text-black/80">{l}</li>
                      ))}
                    </ol>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24 md:py-32 border-b border-black">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <div className="font-mono-label text-black/60">Următorul pas</div>
            <h2 className="mt-3 font-display font-medium uppercase text-5xl md:text-7xl tracking-tighter leading-[0.9]">
              Solicită o<br />
              <span className="italic font-normal">ofertă</span> personalizată.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4 flex lg:justify-end">
            <SmartLink
              to={t("btn.dept.cta.link", url.contact)}
              data-testid="service-cta-contact"
              className="group font-mono-label bg-black text-white px-8 py-5 hover:bg-white hover:text-black border border-black inline-flex items-center gap-3 transition-all"
            >
              Solicită consultanță
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </SmartLink>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

/** Small helper for the Digital dept services grid (linking to these pages). */
export function ServiceCard({ number, title, desc, to, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group bg-white hover:bg-black hover:text-white transition-all p-8 min-h-[220px] flex flex-col justify-between h-full"
    >
      <div className="flex items-start justify-between">
        <div className="font-mono-label opacity-60">{number}</div>
        <ArrowUpRight
          size={22}
          className="opacity-40 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-500"
        />
      </div>
      <div>
        <div className="font-display font-medium uppercase text-3xl md:text-4xl tracking-tighter leading-[0.95]">
          {title}
        </div>
        <div className="mt-3 text-base opacity-70 leading-relaxed max-w-md">{desc}</div>
      </div>
    </Link>
  );
}
